import { useState } from "react";
import ReactGA from "react-ga4";
import {
  updateFilesAction,
  updateGroupFilesAction,
  updatePublicFilesAction,
  updatePrivateFilesAction,
} from "store/actions/files";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  useFetchFiles,
  useLogout,
  useCurrentUser,
  useGetTherapistFiles,
  useGetGroupFiles,
} from "utils/hooks";
import { formatInput } from "utils/helpers";
import { formattedSharedWith } from "./fileUtils";
import { FileResource } from "types";

interface UseUpdateFileProps {
  file: any;
  selectedPatients?: any[] | null;
  groupId?: string;
}

interface argsProps {
  fileUrl: any;
  title: any;
  description: any;
  default: boolean;
  shareWithOrganization: boolean;
}

const useUpdateFile = ({
  file,
  selectedPatients,
  groupId,
}: UseUpdateFileProps) => {
  const [updating, setUpdating] = useState(false);
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false);
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { fetchTherapistFiles, fetchGroupFiles, fetchPrivateFiles } =
    useFetchFiles();
  const { user } = useCurrentUser();
  const { therapistFiles } = useGetTherapistFiles({});
  const { groupFiles } = useGetGroupFiles({ groupId });

  const formatFile = (args: argsProps, file: FileResource) =>
    `{
      uuid: "${file.uuid}",
      title: "${formatInput(args.title)}",
      description: "${formatInput(args.description)}",
      sharedWith: [${formattedSharedWith(selectedPatients)}],
      default: ${args.default ?? false}
      shareWithOrganization: ${args.shareWithOrganization}
    }`;

  const submitUpdateFile = (args: argsProps) => {
    if (!args) {
      return null;
    }

    setUpdating(true);

    const filteredFiles = therapistFiles.filter(
      (therapistFile: { uuid: any }) => therapistFile.uuid !== file.uuid
    );

    const existingUrl = filteredFiles.find(
      (therapistFile: { fileUrl: any }) =>
        therapistFile.fileUrl === args.fileUrl
    );

    if (existingUrl) {
      toast.warn(`A file with this url already exists in your files :)`);
      return null;
    }

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                updateFile(file: ${formatFile(args, file)}) {
                  fileUrl
                  title
                  description
                  uuid
                  addedBy
                  updatedAt
                  sharedWith {
                    label
                    value
                  }
                  default
                  fromBuilder
                  isPrivate
                  privatePatientId
                  shareWithOrganization
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "update_files",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                setUpdating(false);
                logOut();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                setUpdating(false);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              resolve();
              setUpdating(false);
              return true;
            } else {
              fetchTherapistFiles();
              fetchPrivateFiles();
              toast("File Updated");
              setIsUpdateSuccessful(true);
              resolve();
              ReactGA.event("file_updated_success", {
                user_id: user?.id,
              });
              dispatch(updateFilesAction([resData.data.updateFile]));
              dispatch(updatePublicFilesAction([resData.data.updateFile]));
              dispatch(updatePrivateFilesAction([resData.data.updateFile]));
              return setUpdating(false);
            }
          })
          .catch((err) => {
            setUpdating(false);
            console.log(err);
          });
      }, 100);
    });
  };

  const submitUpdateGroupFile = (args: argsProps) => {
    if (!args) {
      return null;
    }

    setUpdating(true);

    const filteredFiles = groupFiles.filter(
      (groupFile: { uuid: any }) => groupFile.uuid !== file.uuid
    );

    const existingUrl = filteredFiles.find(
      (groupFile: { fileUrl: any }) => groupFile.fileUrl === args.fileUrl
    );

    if (existingUrl) {
      toast.warn(`A file with this url already exists in your files :)`);
      return null;
    }

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                updateFile(file: [${formatFile(args, file)}]) {
                  fileUrl
                  title
                  description
                  uuid
                  addedBy
                  updatedAt
                  sharedWith {
                    label
                    value
                  }
                  default
                  fromBuilder
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                setUpdating(false);
                logOut();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                setUpdating(false);
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              resolve();
              setUpdating(false);
              return true;
            } else {
              fetchGroupFiles(groupId);
              toast("File Updated");
              setIsUpdateSuccessful(true);
              resolve();
              ReactGA.event("file_updated_success", {
                user_id: user?.id,
                group_id: groupId,
              });
              dispatch(updateGroupFilesAction([resData.data.updateFile]));
              return setUpdating(false);
            }
          })
          .catch((err) => {
            setUpdating(false);
            console.log(err);
          });
      }, 100);
    });
  };

  return {
    submitUpdateFile,
    submitUpdateGroupFile,
    updating,
    isUpdateSuccessful,
  };
};

export default useUpdateFile;
